import { create } from 'zustand';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  specialty?: string;
  avatar?: string;
  phone?: string;
  clinicId: string;
  permissions: string[];
  isDemo?: boolean;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isReadOnly: boolean;
  setAuth: (user: User) => void;
  setReadOnly: (value: boolean) => void;
  logout: () => void;
  hasPermission: (permission: string) => boolean;
  setLoading: (loading: boolean) => void;
}

const USER_KEY = 'SmartDental_user';
const TOKEN_KEY = 'SmartDental_token';
const READONLY_KEY = 'SmartDental_readonly';

function readStoredUser(): User | null {
  const userRaw = sessionStorage.getItem(USER_KEY);
  if (!userRaw) return null;
  try {
    return JSON.parse(userRaw);
  } catch {
    return null;
  }
}

const storedUser = readStoredUser();
const storedReadOnly = sessionStorage.getItem(READONLY_KEY) === 'true';

export const useAuthStore = create<AuthState>()(
  (set, get) => ({
    user: storedUser,
    isAuthenticated: !!storedUser,
    isLoading: false,
    isReadOnly: storedReadOnly,
    setAuth: (user) => {
      sessionStorage.setItem(USER_KEY, JSON.stringify(user));
      set({ user, isAuthenticated: true });
    },
    setReadOnly: (value) => {
      if (value) {
        sessionStorage.setItem(READONLY_KEY, 'true');
      } else {
        sessionStorage.removeItem(READONLY_KEY);
      }
      set({ isReadOnly: value });
    },
    logout: () => {
      sessionStorage.removeItem(USER_KEY);
      sessionStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(READONLY_KEY);
      set({ user: null, isAuthenticated: false, isReadOnly: false });
    },
    hasPermission: (permission: string) => {
      const user = get().user;
      if (!user) return false;
      if (user.role === 'ADMIN') return true;
      return user.permissions?.includes(permission) ?? false;
    },
    setLoading: (loading) => set({ isLoading: loading }),
  }),
);
